import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import TokenManager from './TokenManager';

class SpotifyApiService {
  private static instance: SpotifyApiService;
  private tokenManager: TokenManager;

  constructor() {
    this.tokenManager = TokenManager.getInstance();
  }

  static getInstance(): SpotifyApiService {
    if (!SpotifyApiService.instance) {
      SpotifyApiService.instance = new SpotifyApiService();
    }
    return SpotifyApiService.instance;
  }

  /**
   * Make authenticated Spotify API call with automatic token management
   * Includes reactive 401 handling for edge cases (manual token tampering, server-side revocation, etc.)
   */
  async makeAuthenticatedRequest<T = any>(
    config: AxiosRequestConfig,
    isRetry: boolean = false
  ): Promise<AxiosResponse<T>> {
    try {
      // Get valid token (refreshes if needed)
      const token = await this.tokenManager.getValidToken();
      
      // Add authorization header
      const requestConfig = {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${token}`
        }
      };

      // Make the request
      return await axios(requestConfig);
      
    } catch (error: any) {
      // Reactive 401 handling: catch expired token errors from Spotify
      if (error.response?.status === 401 && !isRetry) {
        const errorMessage = error.response?.data?.error?.message || '';
        
        // Check if this is specifically an "access token expired" error
        if (errorMessage.toLowerCase().includes('access token expired') || 
            errorMessage.toLowerCase().includes('token expired') ||
            error.response?.data?.error?.status === 401) {
          
          console.warn('⚠️ Spotify returned 401 - token expired. Attempting reactive refresh...');
          
          try {
            // Force refresh the token (bypasses expiry check)
            await this.tokenManager.forceRefresh();
            
            console.log('✅ Token refreshed successfully. Retrying request...');
            
            // Retry the request with the new token (mark as retry to prevent infinite loop)
            return await this.makeAuthenticatedRequest<T>(config, true);
            
          } catch (refreshError) {
            console.error('❌ Reactive token refresh failed:', refreshError);
            // Fall through to throw original error
          }
        }
      }
      
      // If we're here, either:
      // 1. It's not a 401 error
      // 2. It's a 401 but not token expiry
      // 3. It's a retry that also failed
      // 4. The refresh attempt failed
      if (error.response?.status === 401) {
        console.error('🚨 Authentication failed - user needs to re-authenticate');
      }
      
      throw error;
    }
  }

  // Convenience methods for common operations
  async getCurrentUser() {
    return this.makeAuthenticatedRequest({
      method: 'GET',
      url: 'https://api.spotify.com/v1/me'
    });
  }

  async getUserPlaylists(limit = 50, offset = 0) {
    return this.makeAuthenticatedRequest({
      method: 'GET',
      url: `https://api.spotify.com/v1/me/playlists?limit=${limit}&offset=${offset}`
    });
  }

  async getPlaylist(playlistId: string) {
    return this.makeAuthenticatedRequest({
      method: 'GET',
      url: `https://api.spotify.com/v1/playlists/${playlistId}`
    });
  }

  async getPlaylistTracks(playlistId: string, limit = 50, offset = 0) {
    return this.makeAuthenticatedRequest({
      method: 'GET',
      url: `https://api.spotify.com/v1/playlists/${playlistId}/items?limit=${limit}&offset=${offset}`
    });
  }

  async getTrack(trackId: string) {
    return this.makeAuthenticatedRequest({
      method: 'GET',
      url: `https://api.spotify.com/v1/tracks/${trackId}`
    });
  }

  async searchTracks(query: string, limit = 20, offset = 0) {
    return this.makeAuthenticatedRequest({
      method: 'GET',
      url: `https://api.spotify.com/v1/search?q=${encodeURIComponent(query)}&type=track&limit=${limit}&offset=${offset}`
    });
  }

  async searchPlaylists(query: string, limit = 20, offset = 0) {
    return this.makeAuthenticatedRequest({
      method: 'GET',
      url: `https://api.spotify.com/v1/search?q=${encodeURIComponent(query)}&type=playlist&limit=${limit}&offset=${offset}`
    });
  }

  async createPlaylist(name: string, description?: string, isPublic = false) {
    return this.makeAuthenticatedRequest({
      method: 'POST',
      url: 'https://api.spotify.com/v1/me/playlists',
      data: {
        name,
        description,
        public: isPublic
      }
    });
  }

  async addTracksToPlaylist(playlistId: string, trackUris: string[]) {
    return this.makeAuthenticatedRequest({
      method: 'POST',
      url: `https://api.spotify.com/v1/playlists/${playlistId}/items`,
      data: {
        uris: trackUris
      }
    });
  }

  async removeTracksFromPlaylist(playlistId: string, trackUris: string[]) {
    return this.makeAuthenticatedRequest({
      method: 'DELETE',
      url: `https://api.spotify.com/v1/playlists/${playlistId}/items`,
      data: {
        items: trackUris.map(uri => ({ uri }))
      }
    });
  }

  async reorderPlaylistTracks(playlistId: string, rangeStart: number, insertBefore: number, rangeLength = 1) {
    return this.makeAuthenticatedRequest({
      method: 'PUT',
      url: `https://api.spotify.com/v1/playlists/${playlistId}/items`,
      data: {
        range_start: rangeStart,
        insert_before: insertBefore,
        range_length: rangeLength
      }
    });
  }

  async updatePlaylistDetails(playlistId: string, name?: string, description?: string, isPublic?: boolean) {
    const data: any = {};
    if (name !== undefined) data.name = name;
    if (description !== undefined) data.description = description;
    if (isPublic !== undefined) data.public = isPublic;

    return this.makeAuthenticatedRequest({
      method: 'PUT',
      url: `https://api.spotify.com/v1/playlists/${playlistId}`,
      data
    });
  }

  /**
   * Generic method for custom Spotify API requests
   */
  async makeCustomRequest<T = any>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.makeAuthenticatedRequest<T>(config);
  }

  /**
   * Make Spotify API request with a specific token (for special cases like share links)
   */
  async makeRequestWithToken<T = any>(
    config: AxiosRequestConfig,
    token: string
  ): Promise<AxiosResponse<T>> {
    const requestConfig = {
      ...config,
      headers: {
        ...config.headers,
        Authorization: `Bearer ${token}`
      }
    };

    return await axios(requestConfig);
  }
}

export default SpotifyApiService;
